import React from 'react';
import { IStorefrontSection } from '../../Api/Models/Products';
import {
    IBannerContent,
    IDividerContent,
    IRichTextContent,
    ISingleProductContent,
    IVideoContent,
    parseContentConfig,
} from '../Helpers/contentSectionConfig';
import VideoSection from './VideoSection';
import RichTextSection from './RichTextSection';
import BannerSection from './BannerSection';
import SingleProductSection from './SingleProductSection';
import { resolveStyleProps } from '../Helpers/stylesConfig';
import { isFractionalWidth } from './SectionRowLayout';

interface ContentSectionProps {
    section: IStorefrontSection;
    // Threaded to BannerSection for full-bleed overlay framing / neutral fallback fill.
    contentMaxWidth?: number;
    storefrontBackgroundColor?: string;
}

// Dispatches a content section to its type-specific renderer. Owns the section chrome common to all
// content sections: the optional section title (styled by section.titleStyleProps, matching the
// product-section title convention) and the section.styleProps container. Banner sections manage
// their own width/full-bleed, so they skip the standard horizontal padding that would otherwise
// offset the full-bleed breakout.
const ContentSection: React.FC<ContentSectionProps> = ({ section, contentMaxWidth, storefrontBackgroundColor }) => {
    // Named-style resolution for the section's own chrome: `styleProps` resolves a section style's
    // `band` part and `titleStyleProps` its `title` part, each merged UNDER the local dict, with the
    // reserved `$styleRef` key stripped. No ref => the same objects, unchanged rendering.
    const sectionStyleProps = resolveStyleProps(section.styleProps, 'band');
    const sectionTitleStyleProps = resolveStyleProps(section.titleStyleProps, 'title');
    const isBanner = section.type === 'banner';
    const bannerConfig = isBanner ? parseContentConfig<IBannerContent>(section.contentConfig) : null;
    // A fractional section shares its row with siblings, so a 100vw full-bleed breakout from inside
    // its column would escape the column and overlap them. Ignore full-bleed there (both for the
    // banner itself and for the chrome-stripping branch below) and render the banner contained.
    const inFractionalColumn = isFractionalWidth(section.width);
    const bannerFullBleed = bannerConfig?.fullBleed === true && !inFractionalColumn;

    let body: React.ReactNode = null;
    switch (section.type) {
        case 'video':
            body = <VideoSection config={parseContentConfig<IVideoContent>(section.contentConfig)} />;
            break;
        case 'richText':
            body = (
                <RichTextSection
                    section={section}
                    legacyConfig={parseContentConfig<IRichTextContent>(section.contentConfig)}
                />
            );
            break;
        case 'banner':
            body = (
                <BannerSection
                    config={bannerConfig}
                    contentMaxWidth={contentMaxWidth}
                    storefrontBackgroundColor={storefrontBackgroundColor}
                    disableFullBleed={inFractionalColumn}
                />
            );
            break;
        case 'singleProduct':
            // Unlike video/banner, this renderer needs the section's already-resolved product
            // (section.products[0]) alongside the parsed presentation config, so pass the section.
            body = (
                <SingleProductSection
                    section={section}
                    config={parseContentConfig<ISingleProductContent>(section.contentConfig)}
                />
            );
            break;
        case 'divider': {
            // Pure presentation — no products, no fetching. A centered line at widthPercent of the
            // column with `spacing`px of vertical padding; lineStyle 'none' renders only the spacer.
            const cfg = parseContentConfig<IDividerContent>(section.contentConfig) ?? {};
            const lineStyle = cfg.lineStyle ?? 'solid';
            const thickness = cfg.thickness ?? 2;
            const widthPercent = Math.min(100, Math.max(1, cfg.widthPercent ?? 80));
            const spacing = cfg.spacing ?? 24;
            const color = cfg.color ?? 'rgba(0,0,0,0.15)';
            body = (
                <div style={{ width: `${widthPercent}%`, margin: '0 auto', padding: `${spacing}px 0` }}>
                    {lineStyle !== 'none' && <div style={{ borderTop: `${thickness}px ${lineStyle} ${color}` }} />}
                </div>
            );
            break;
        }
        default:
            return null;
    }

    // Full-bleed banner: BannerSection returns a 100vw breakout (marginLeft: calc(50% - 50vw)) that
    // must escape the max-width content column to reach the viewport edges. The standard chrome
    // wrapper below applies section.styleProps, which a merchant config can make establish a clip /
    // containing context (overflow, transform, border-radius + overflow) — that traps the 100vw child
    // inside the column and is the reported "full width doesn't work". Render a full-bleed banner with
    // NO styleProps chrome around the breakout (the banner owns its own edge-to-edge box), mirroring
    // how HomePage renders the full-bleed hero as a direct, unconstrained child of the content column.
    // The title stays padded in from the full-bleed image edge.
    if (bannerFullBleed) {
        return (
            <div style={{ marginBottom: '1em' }}>
                {!section.hideTitle && section.title && (
                    <div style={{ padding: '0 1em 0.5em', ...sectionTitleStyleProps }}>
                        <div>{section.title}</div>
                        {section.subtitle && <div style={{ fontSize: '0.65em', opacity: 0.85, fontWeight: 'normal' }}>{section.subtitle}</div>}
                    </div>
                )}
                {body}
            </div>
        );
    }

    return (
        <div style={{ padding: isBanner ? 0 : '1em', marginBottom: '1em', ...sectionStyleProps }}>
            {!section.hideTitle && section.title && (
                // No default typography — mirror the product-section title, which is styled solely by
                // titleStyleProps. A banner keeps its title padded in from the full-bleed image edge.
                <div style={{ ...(isBanner ? { padding: '0 1em 0.5em' } : { marginBottom: '0.5em' }), ...sectionTitleStyleProps }}>
                    <div>{section.title}</div>
                    {section.subtitle && <div style={{ fontSize: '0.65em', opacity: 0.85, fontWeight: 'normal' }}>{section.subtitle}</div>}
                </div>
            )}
            {body}
        </div>
    );
};

export default ContentSection;
